import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commonUtils,
  useDialogs,
  useNotifications,
  useEditorStore,
  useFlowBuilder2DeleteFlowStepMutation,
} from '@diana/core';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';

export const useDeleteStep = (
  options?: Parameters<typeof useFlowBuilder2DeleteFlowStepMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { confirm } = useDialogs();
  const notifications = useNotifications();

  const getFlow = useEditorStore((s) => s.getFlow);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateFlow = useEditorStore((s) => s.updateFlow);
  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);

  const { mutateAsync, isPending: isDeletingStep } = useFlowBuilder2DeleteFlowStepMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      setActiveTaskId(null);
      updateFlow((draft) => ({ ...draft, steps: data.steps }));
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      notifications.error({
        message: t('errors.deleteStepFailed'),
        description: commonUtils.getErrorMessage(error),
      });
      options?.onError?.(error, variables, context);
    },
  });

  const deleteStep = useCallback(
    async (args: { stepId: string; skipConfirm?: boolean }) => {
      const { stepId, skipConfirm } = args;

      const flow = getFlow();
      if (!flow) return false;

      const steps = flow.steps.filter((step) => step.stepId === stepId);
      if (!steps.length) return false;

      const hasAnyComponentInStep = steps.some((step) => {
        const layout = getLayoutById(step.layoutId);
        if (!layout) return false;
        return layout?.components?.length > 1;
      });

      if (!skipConfirm && hasAnyComponentInStep) {
        const { confirmed } = await confirm(null, {
          title: t('prompts.areYouSureToDeleteSubject', {
            subject: steps[0].stepDisplayName || t('labels.untitledStep'),
          }),
          variant: 'delete',
        });
        if (!confirmed) return false;
      }

      await Promise.all(
        steps.map((step) => {
          return mutateAsync({
            flowId: flow.id,
            flowStepId: step.id,
          });
        }),
      );
      return true;
    },
    [t, getFlow, getLayoutById, confirm, mutateAsync],
  );

  return { deleteStep, isDeletingStep };
};
